import {
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CardHeader,
  colors,
  Divider,
  Link,
  Table,
  TableBody,
  Typography,
} from "@material-ui/core";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import { makeStyles } from "@material-ui/styles";
import clsx from "clsx";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import Navigation from "../Navigation";
import { SET_SINGLE_DESTINATION_CONFIG } from "../reducers/destinations";
import {
  DestinationConfigRow,
  DestinationHIPAARow,
} from "./configs/shared/ConfigPageRow";
import {
  getHippaFieldsForDestinations,
  HIPAA,
  saasDestinations,
} from "./destinations";

import { ConfigureDestinationsModal } from "./modals/ConfigureDestinationsModal";

const useStyles = makeStyles((theme) => ({
  navigation: {
    paddingTop: 12,
  },
  card: {
    width: "50%",
    minWidth: 800,
    minHeight: 500,
  },
  settingsTitle: {
    marginTop: 40,
    marginLeft: 30,
    marginBottom: 20,
  },
  cardContent: {
    padding: 0,
    "&:last-child": {
      paddingBottom: 0,
    },
  },
  body: {
    margin: 24,
  },
  headerAction: {
    marginTop: 0,
  },
  headerIcon: {
    marginRight: theme.spacing(2),
  },
  titleWrapper: {
    display: "flex",
    alignItems: "center",
  },
  lightText: {
    color: "#546E7A",
  },
  darkText: {
    color: "#263238",
  },
  breadcrumb: {
    marginTop: 5,
    marginBottom: 10,
  },
}));

export function getInitialMapping(mappingKey, configuredDest) {
  let initialMapping = configuredDest.mapping || [];

  if (
    mappingKey &&
    configuredDest &&
    configuredDest.settings &&
    configuredDest.settings[mappingKey]
  ) {
    initialMapping = _.uniqBy(
      _.concat(initialMapping, configuredDest.settings[mappingKey]),
      function (mapping) {
        return `${mapping.type || "eventName"}|${mapping.fpEvent}|${
          mapping.destEvent
        }`;
      }
    );
  }
  return initialMapping;
}

function TransformsConfigRow({ destination, transformsExist, transformCount }) {
  const history = useHistory();

  const onOpenConfigModal = () => {
    history.push(`/transformations/destination/${destination.slug}`);
  };

  return (
    <>
      <DestinationConfigRow
        name="Event Transformations"
        labelColor={transformsExist ? colors.green[600] : colors.orange[600]}
        labelText={
          transformsExist
            ? `${transformCount} ${
                transformCount === 1 ? "Transform" : "Transforms"
              }`
            : "No Transforms"
        }
        onOpenConfigModal={onOpenConfigModal}
      />
    </>
  );
}

TransformsConfigRow.propTypes = {
  destination: PropTypes.object,
  transformsExist: PropTypes.bool,
  transformCount: PropTypes.number,
  onOpenConfigModal: PropTypes.func,
};

const DefaultDestinationConfigTableBody = ({
  destination,
  idSet,
  idPartiallySet,
  onOpenConfigModal,
  transformsExist,
  transformCount,
  settings,
}) => {
  const onClickHipaaConfigure = useCallback(() => {
    onOpenConfigModal(HIPAA);
  }, [onOpenConfigModal]);

  return (
    <TableBody>
      <DestinationConfigRow
        name={destination.configName}
        labelColor={idSet ? colors.green[600] : colors.red[600]}
        labelText={
          idSet
            ? "Configured"
            : idPartiallySet
            ? "Needs Attention"
            : "Not Configured"
        }
        onOpenConfigModal={onOpenConfigModal}
      />
      {destination.supportsHipaa && (
        <DestinationHIPAARow
          onOpenConfigModal={onClickHipaaConfigure}
          settings={settings}
        />
      )}
      {destination.supportsTrack && (
        <TransformsConfigRow
          destination={destination}
          transformsExist={transformsExist}
          transformCount={transformCount}
        />
      )}
    </TableBody>
  );
};

DefaultDestinationConfigTableBody.propTypes = {
  destination: PropTypes.any,
  idSet: PropTypes.bool,
  idPartiallySet: PropTypes.bool,
  onOpenConfigModal: PropTypes.func,
  transformsExist: PropTypes.bool,
  transformCount: PropTypes.number,
  settings: PropTypes.object,
};

function CustomDestinationConfigTableBody({
  destination,
  transformsExist,
  transformCount,
  handleUpdateConfigureDest,
  settings,
}) {
  const { customConfig } = destination;
  const [openModal, setOpenModal] = useState("");
  const closeModal = () => setOpenModal("");
  const configuredDest = useSelector(
    (state) => state.destinations.data[destination.slug]
  );

  return (
    <TableBody>
      {customConfig.RowOrder.map((rowName, i) => {
        const RowComponent = customConfig.Rows[rowName];
        const ModalComponent = customConfig.Modals[rowName];
        const setModalOpen = () => setOpenModal(rowName);
        return (
          <React.Fragment key={i}>
            <RowComponent
              key={`${i}-row`}
              destination={destination}
              setConfigModalOpen={setModalOpen}
            />
            {openModal && (
              <>
                {openModal === HIPAA ? (
                  <ConfigureDestinationsModal
                    includeHeader={false}
                    open={true}
                    onClose={closeModal}
                    onSave={handleUpdateConfigureDest}
                    dest={destination}
                    settings={configuredDest ? configuredDest.settings : {}}
                    configFields={getHippaFieldsForDestinations(destination)}
                  />
                ) : (
                  <ModalComponent
                    key={`${i}-modal`}
                    destination={destination}
                    open={openModal === rowName}
                    onClose={closeModal}
                    handleUpdateConfigureDest={handleUpdateConfigureDest}
                  />
                )}
              </>
            )}
          </React.Fragment>
        );
      })}
      {destination.supportsHipaa && (
        <DestinationHIPAARow
          onOpenConfigModal={() => setOpenModal(HIPAA)}
          settings={settings}
        />
      )}
      {destination.supportsTrack && (
        <TransformsConfigRow
          destination={destination}
          transformsExist={transformsExist}
          transformCount={transformCount}
        />
      )}
    </TableBody>
  );
}

CustomDestinationConfigTableBody.propTypes = {
  destination: PropTypes.any,
  transformsExist: PropTypes.bool,
  transformCount: PropTypes.number,
  handleUpdateConfigureDest: PropTypes.func,
  settings: PropTypes.object,
};

function DestinationConfigPage({ destination }) {
  const DestinationIcon = destination.logoSquare;
  const history = useHistory();
  const classes = useStyles();

  const dispatch = useDispatch();
  const configuredDest = useSelector(
    (state) => state.destinations.data[destination.slug]
  );
  const loading = useSelector((state) => state.destinations.loading);
  const [configModalOpen, setConfigModalOpen] = useState(false);

  const configModalFields = useMemo(() => {
    switch (configModalOpen) {
      case HIPAA:
        return getHippaFieldsForDestinations(destination);
      default:
        return destination.configFields;
    }
  }, [configModalOpen, destination]);

  useEffect(() => {
    if (loading) {
      return;
    }

    // If there's no dest config set for this destination, initialize it.
    if (!configuredDest) {
      setConfiguredDest({ settings: {} });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, configuredDest]);

  const setConfiguredDest = (destConfig) => {
    dispatch({
      type: SET_SINGLE_DESTINATION_CONFIG,
      payload: {
        [destination.slug]: destConfig,
      },
    });
  };

  if (!configuredDest) {
    return null;
  }

  const allRequiredConfigKeys = _.chain(destination.configFields)
    .filter(({ required }) => required)
    .map(({ key }) => key)
    .value();
  const idSet =
    !loading &&
    _.every(allRequiredConfigKeys, (key) => key in configuredDest.settings);
  const idPartiallySet =
    !loading &&
    _.some(allRequiredConfigKeys, (key) => key in configuredDest.settings);
  const initialMapping = configuredDest.mapping || [];
  const transformCount = initialMapping.length;
  const transformsExist = !loading && transformCount > 0;

  function handleUpdateConfigureDest(destConfig) {
    const { settings } = destConfig;
    const newSettings = _.extend({}, configuredDest.settings || {}, settings);
    const newConfiguredDestState = _.extend(
      { mapping: [], settings: {} },
      configuredDest,
      destConfig,
      {
        settings: newSettings,
      }
    );
    setConfiguredDest(newConfiguredDestState);
  }

  const title = `${destination.name}: Configuration Settings`;

  return (
    <>
      <Card className={clsx(classes.card)}>
        <CardHeader
          title={
            <div className={classes.titleWrapper}>
              <DestinationIcon
                className={clsx(classes.headerIcon)}
                height={24}
                width={24}
              />
              {title}
            </div>
          }
          action={
            <Button variant="outlined" onClick={() => history.goBack()}>
              Back
            </Button>
          }
          classes={{ action: clsx(classes.headerAction) }}
        />
        <Divider />
        {!loading && (
          <CardContent className={clsx(classes.cardContent)}>
            <div className={clsx(classes.body)}>
              {destination.configBodyHelp}
            </div>
            <Divider />
            <Table>
              {destination.customConfig ? (
                <>
                  <CustomDestinationConfigTableBody
                    destination={destination}
                    transformsExist={transformsExist}
                    transformCount={transformCount}
                    handleUpdateConfigureDest={handleUpdateConfigureDest}
                    settings={configuredDest ? configuredDest.settings : {}}
                  />
                </>
              ) : (
                <DefaultDestinationConfigTableBody
                  destination={destination}
                  idSet={idSet}
                  idPartiallySet={idPartiallySet}
                  onOpenConfigModal={setConfigModalOpen}
                  transformsExist={transformsExist}
                  transformCount={transformCount}
                  settings={configuredDest ? configuredDest.settings : {}}
                />
              )}
            </Table>
          </CardContent>
        )}
      </Card>

      {configModalOpen && (
        <ConfigureDestinationsModal
          includeHeader={configModalOpen !== HIPAA}
          open={configModalOpen}
          onClose={() => setConfigModalOpen(false)}
          onSave={(dest, id, destConfig) =>
            handleUpdateConfigureDest(destConfig)
          }
          dest={destination}
          settings={configuredDest ? configuredDest.settings : {}}
          configFields={configModalFields}
        />
      )}
    </>
  );
}

DestinationConfigPage.propTypes = {
  destination: PropTypes.object.isRequired,
};

const DestinationConfigPageWrapper = ({ destinationSlug }) => {
  const history = useHistory();
  const classes = useStyles();

  const destination = useMemo(() => saasDestinations[destinationSlug], [
    destinationSlug,
  ]);
  const category = useMemo(() => destination.categoryDisplay(), [destination]);
  const goToCategory = useCallback(
    () => history.push(`/destinations/${category}`),
    [category, history]
  );

  return (
    <Navigation className={clsx(classes.navigation)}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        className={clsx(classes.breadcrumb)}
      >
        <Link color="inherit" onClick={() => history.push(`/destinations`)}>
          <Typography className={clsx(classes.lightText)} variant="overline">
            Destinations
          </Typography>
        </Link>
        <Link color="inherit" onClick={goToCategory}>
          <Typography className={clsx(classes.lightText)} variant="overline">
            {destination.categoryDisplay().toUpperCase()}
          </Typography>
        </Link>
        <Typography className={clsx(classes.darkText)} variant="overline">
          {destination.name.toUpperCase()}
        </Typography>
      </Breadcrumbs>
      <DestinationConfigPage destination={destination} />
    </Navigation>
  );
};

DestinationConfigPageWrapper.propTypes = {
  destinationSlug: PropTypes.string.isRequired,
};

export default DestinationConfigPageWrapper;
